/**
 * Row types in the variant table
 *
 * HOW TO ADD A NEW ROW:
 * 1. Add enum value here (e.g., MyNewRow = "myNewRow")
 * 2. Add calculation in ~/utils/variantTable/rowCalculators.ts (switch case)
 * 3. Wire up in ~/hooks/useVariantRowData.ts (call calculateRowData)
 * 4. Add to finalRows in VariantTableCard.tsx with skuLevel flag
 * 5. Optional: Add custom rendering in cellRenderers/DateCellRenderer.tsx
 * 6. Add tests in ~/utils/test/rowCalculators.test.ts
 *
 * FOR SKU-LEVEL DATA:
 * - If data exists at variant level, set skuLevel: true in finalRows (auto-calculated)
 * - If needs new backend data, add query to ~/server/api/routers/variantTable.ts
 * - Calculation logic is shared - works for both variant and SKU automatically
 */
export enum VariantsTableRow {
  RawForecasts = "rawForecasts",
  Forecast = "forecast",
  Sales = "sales",
  SalesIndex = "salesIndex",
  NetSales = "netSales",
  PreOrders = "preOrders",
  Returns = "returns",
  ClosingStock = "closingStock",
  WeeksOnHand = "weeksOnHand",
  IncomingStock = "incomingStock",
  ArrivedStock = "arrivedStock",
  SuggestedOrders = "suggestedOrders",
  SuggestedIncomingStock = "suggestedIncomingStock",
  OptimalStock = "optimalStock",
  Availability = "availability",
  ForecastedReturns = "forecastedReturns",
  // Performance tab — values are computed outside the standard variant-table
  // pipeline and injected via VariantTableCard's `extraRowData` prop.
  PerformanceIndex = "performanceIndex",
  ExpectedDemand = "expectedDemand",
}

import type { AvailabilityPeriodBreakdown, ClosingStockBreakdown } from "~/utils/stockProjection"

export type { ClosingStockBreakdown }

/**
 * Data structure for a cell in the product table
 */
export type ProductTableData = {
  value: number | null
  ourEstimate?: boolean
  exceedsOneYear?: boolean
  hasPastIncomingOrder?: boolean
  noComparisonData?: boolean
  benchmarkValue?: number
  currentValue?: number
  dateRange?: { start: Date; end: Date }
  benchmarkDateRange?: { start: Date; end: Date }
  orders?: {
    skuId: string
    orderDate: Date
    quantity: number
    warehouseId: string
  }[]
  isLoading?: boolean
  stockBreakdown?: ClosingStockBreakdown
  /** Availability row only; the chains are already reconciled by `periodBreakdownForKeys`. */
  forecast?: number
  stockoutLoss?: number
  availabilityBreakdown?: AvailabilityPeriodBreakdown
}
